import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Modal from "components/modal/modal";
import FormInput from "components/input/form-input";
import FormSelect from "components/input/form-select";
import FormTextarea from "components/input/form-textarea";
import FormCheckbox from "components/input/form-checkbox";
import { orgTypeOptions, timelineOptions } from "components/modal/use-org-form";
import FormSuccess from "components/modal/form-success";
import { FORMSPREE_KEYS, formspreeUrl } from "config/formspree";

const areas = [
  "Software Engineering",
  "DevOps Engineering",
  "Data and AI",
  "Business Analysis",
  "Custom Hiring",
];

const schema = yup.object({
  orgName: yup.string().required("Organization name is required"),
  contact: yup.string().required("Contact person is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  phone: yup.string().required("Phone number is required"),
  orgType: yup.string(),
  timeline: yup.string(),
  additional: yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

interface Props {
  open: boolean;
  handleClose: () => void;
}

const HireTalentModal = ({ open, handleClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema as any),
  });

  const toggleArea = (area: string) =>
    setSelectedAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const res = await fetch(formspreeUrl(FORMSPREE_KEYS.hireTalent), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          organizationName: data.orgName,
          contactPerson: data.contact,
          email: data.email,
          phone: data.phone,
          organizationType: data.orgType,
          expectedTimeline: data.timeline,
          areaOfInterest: selectedAreas.join(", "),
          additionalInformation: data.additional,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
      reset();
      setSelectedAreas([]);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    reset();
    setSelectedAreas([]);
    setSubmitted(false);
    setSubmitError(null);
    handleClose();
  };

  return (
    <Modal open={open} handleClose={onClose} className="w-[560px] lg:p-8 p-6">
      {submitted ? (
        <FormSuccess
          title="Request Sent!"
          message="Thank you! We've received your hiring request and will be in touch shortly."
          onClose={onClose}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
          <div className="flex flex-col gap-1 pr-8">
            <h2 className="text-[28px] font-bold text-[#1A1A2E] leading-tight">Hire Talents</h2>
            <p className="text-sm text-[#6B6B6B]">
              Tell us about your need and how we can provide the best solution for you
            </p>
          </div>

          <section className="max-h-[75vh] w-full flex flex-col gap-4 overflow-y-auto scrollbar-none">
            <FormInput
              label="Organization Name"
              placeholder="Abc Co-operative"
              register={register("orgName")}
              error={errors.orgName}
            />
            <FormInput
              label="Contact Person"
              placeholder="John Doe"
              register={register("contact")}
              error={errors.contact}
            />
            <FormInput
              label="Email Address"
              type="email"
              placeholder="abc@gmail.com"
              register={register("email")}
              error={errors.email}
            />
            <FormInput
              label="Phone number"
              placeholder="+1 234 567 8900"
              register={register("phone")}
              error={errors.phone}
            />
            <div className="grid grid-cols-2 gap-3">
              <Controller
                name="orgType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Organization type"
                    placeholder="Select an option"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={orgTypeOptions}
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="timeline"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Expected Timeline"
                    placeholder="Select an option"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={timelineOptions}
                    error={fieldState.error}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-dark-100">
                Area of Interest <span className="text-[#9E9E9E]">(Select all that apply)</span>
              </p>
              {areas.map((area) => (
                <FormCheckbox
                  key={area}
                  label={area}
                  checked={selectedAreas.includes(area)}
                  onChange={() => toggleArea(area)}
                />
              ))}
            </div>
            <FormTextarea
              label="Additional Information"
              placeholder="Write your message"
              register={register("additional")}
              error={errors.additional}
            />
          </section>

          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-12 px-8 rounded-full bg-[#CBA135] text-[#1A1A2E] font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 w-fit"
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default HireTalentModal;
